import { useCallback, useMemo } from 'react';

import {
  useCreateFeatureRequestMutation,
  useGetFeatureRequestsQuery,
  useToggleFeatureRequestVoteMutation,
  useUpdateFeatureRequestMutation,
  useUpdateFeatureRequestStatusMutation,
} from '@/services/api/featureFeedbackService';

import { findSimilarFeatureRequests } from './featureFeedbackAlgorithms';
import type {
  AddFeatureRequestInput,
  FeatureRequest,
  FeatureRequestStatus,
  ViewerIdentity,
} from './types';

const AUTO_GROUP_THRESHOLD = 0.4;

function createViewerNameFallback(): string {
  return 'Anonymous teammate';
}

export type AddFeatureRequestResult =
  | {
      type: 'created';
      request: FeatureRequest;
    }
  | {
      type: 'grouped';
      matchedRequest: FeatureRequest;
      similarityScore: number;
    }
  | {
      type: 'already_supported';
      matchedRequest: FeatureRequest;
      similarityScore: number;
    };

interface UseFeatureFeedbackBoardResult {
  viewer: ViewerIdentity;
  requests: FeatureRequest[];
  totalVotes: number;
  activeVoterCount: number;
  addRequest: (input: AddFeatureRequestInput) => Promise<AddFeatureRequestResult>;
  toggleVote: (requestId: string) => Promise<void>;
  setRequestStatus: (
    requestId: string,
    status: FeatureRequestStatus,
  ) => Promise<void>;
  updateRequest: (
    requestId: string,
    payload: Pick<FeatureRequest, 'title' | 'description'>,
  ) => Promise<void>;
}

export function useFeatureFeedbackBoard(
  preferredViewerName?: string,
  preferredViewerId?: string,
): UseFeatureFeedbackBoardResult {
  const viewer = useMemo<ViewerIdentity>(
    () => ({
      id: preferredViewerId?.trim() || 'current-user',
      name: preferredViewerName?.trim() || createViewerNameFallback(),
    }),
    [preferredViewerId, preferredViewerName],
  );

  const { data: requests = [] } = useGetFeatureRequestsQuery();
  const [createFeatureRequest] = useCreateFeatureRequestMutation();
  const [toggleFeatureRequestVote] = useToggleFeatureRequestVoteMutation();
  const [updateFeatureRequestStatus] = useUpdateFeatureRequestStatusMutation();
  const [updateFeatureRequest] = useUpdateFeatureRequestMutation();

  const addRequest = useCallback(
    async (input: AddFeatureRequestInput): Promise<AddFeatureRequestResult> => {
      const nowIso = new Date().toISOString();
      const title = input.title.trim();
      const description = input.description.trim();

      const candidateForSimilarity: FeatureRequest = {
        id: 'draft',
        title,
        description,
        category: input.category,
        status: 'proposed',
        createdAt: nowIso,
        updatedAt: nowIso,
        createdBy: viewer.name,
        createdById: viewer.id,
        votes: [],
      };

      const [similarMatch] = findSimilarFeatureRequests(
        candidateForSimilarity,
        requests,
        AUTO_GROUP_THRESHOLD,
        1,
      );

      if (!similarMatch) {
        const createdRequest = await createFeatureRequest({
          title,
          description,
          category: input.category,
        }).unwrap();

        return {
          type: 'created',
          request: createdRequest,
        };
      }

      const matchedRequest = similarMatch.request;
      const alreadySupported = matchedRequest.votes.some(
        (vote) => vote.voterId === viewer.id,
      );

      if (alreadySupported) {
        return {
          type: 'already_supported',
          matchedRequest,
          similarityScore: similarMatch.score,
        };
      }

      const updatedRequest = await toggleFeatureRequestVote({
        requestId: matchedRequest.id,
      }).unwrap();

      return {
        type: 'grouped',
        matchedRequest: updatedRequest,
        similarityScore: similarMatch.score,
      };
    },
    [createFeatureRequest, requests, toggleFeatureRequestVote, viewer.id, viewer.name],
  );

  const toggleVote = useCallback(
    async (requestId: string): Promise<void> => {
      await toggleFeatureRequestVote({ requestId }).unwrap();
    },
    [toggleFeatureRequestVote],
  );

  const setRequestStatus = useCallback(
    async (requestId: string, status: FeatureRequestStatus): Promise<void> => {
      await updateFeatureRequestStatus({ requestId, status }).unwrap();
    },
    [updateFeatureRequestStatus],
  );

  const updateRequest = useCallback(
    async (
      requestId: string,
      payload: Pick<FeatureRequest, 'title' | 'description'>,
    ): Promise<void> => {
      await updateFeatureRequest({
        requestId,
        title: payload.title.trim(),
        description: payload.description.trim(),
      }).unwrap();
    },
    [updateFeatureRequest],
  );

  const totalVotes = useMemo(
    () => requests.reduce((sum, request) => sum + request.votes.length, 0),
    [requests],
  );

  const activeVoterCount = useMemo(() => {
    const voterIds = new Set(
      requests.flatMap((request) => request.votes.map((vote) => vote.voterId)),
    );
    return voterIds.size;
  }, [requests]);

  return {
    viewer,
    requests,
    totalVotes,
    activeVoterCount,
    addRequest,
    toggleVote,
    setRequestStatus,
    updateRequest,
  };
}
